/**
 * AdvancedDropdown.tsx - Enhanced Dropdown Menu Component
 * 
 * This component provides an advanced dropdown menu with search functionality,
 * multi-select support, and custom option rendering capabilities.
 * 
 * Key Features:
 * - Search and filter options
 * - Multi-select with checkboxes
 * - Custom option rendering
 * - Keyboard navigation support
 * - Click-outside detection for closing
 * - Responsive positioning
 * 
 * Props:
 * - options: Array<{value: string, label: string}> - Dropdown options
 * - value?: string | string[] - Selected value(s)
 * - onChange: (value: string | string[]) => void - Change handler
 * - placeholder?: string - Placeholder text
 * - searchable?: boolean - Enable search functionality
 * - multiSelect?: boolean - Enable multi-selection
 * - disabled?: boolean - Disable the dropdown
 * 
 * Dependencies: Styled-components, Lucide icons, click-outside detection
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, X } from 'lucide-react';

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownButton = styled.button<{ isOpen: boolean; hasValues: boolean }>`
  width: 100%;
  min-height: 48px;
  padding: 12px 56px 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: #ffffff;
  color: ${props => props.hasValues ? '#1e293b' : '#9ca3af'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  text-align: left;
  justify-content: flex-start;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const PlaceholderText = styled.span`
  color: #9ca3af;
  font-size: 16px;
`;

const SelectedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #0ea5e9;
`;

const BadgeRemoveButton = styled.button`
  background: none;
  border: none;
  color: #0369a1;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 16px;
  height: 16px;

  &:hover {
    background: rgba(3, 105, 161, 0.1);
  }
`;

const DropdownIcons = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: none;
`;

const ClearAllButton = styled.button<{ visible: boolean }>`
  background: #f1f5f9;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 20px;
  height: 20px;
  pointer-events: auto;

  &:hover {
    background: #e2e8f0;
    color: #374151;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.9);
  }
`;

const ChevronIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  width: 16px;
  height: 16px;
  color: #64748b;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 99999;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const DropdownOption = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: ${props => props.isSelected ? '#f0f9ff' : 'transparent'};
  color: #374151;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${props => props.isSelected ? '#e0f2fe' : '#f1f5f9'};
  }

  &:focus {
    outline: none;
    background: ${props => props.isSelected ? '#e0f2fe' : '#e0f2fe'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

interface AdvancedDropdownProps {
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
}

export const AdvancedDropdown: React.FC<AdvancedDropdownProps> = ({
  placeholder,
  options,
  selectedValues,
  onSelectionChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (value: string) => {
    if (selectedValues.includes(value)) {
      // Remove if already selected
      onSelectionChange(selectedValues.filter(v => v !== value));
    } else {
      // Add if not selected
      onSelectionChange([...selectedValues, value]);
    }
  };

  const handleRemoveBadge = (valueToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange(selectedValues.filter(v => v !== valueToRemove));
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange([]);
  };

  // Filter out selected options from dropdown
  const availableOptions = options.filter(option => !selectedValues.includes(option.value));

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton
        isOpen={isOpen}
        hasValues={selectedValues.length > 0}
        onClick={handleToggleDropdown}
      >
        {selectedValues.length === 0 ? (
          <PlaceholderText>{placeholder}</PlaceholderText>
        ) : (
          selectedValues.map(value => {
            const option = options.find(opt => opt.value === value);
            return (
              <SelectedBadge key={value}>
                {option?.label || value}
                <BadgeRemoveButton
                  onClick={(e) => handleRemoveBadge(value, e)}
                >
                  <X size={12} />
                </BadgeRemoveButton>
              </SelectedBadge>
            );
          })
        )}
        
        <DropdownIcons>
          <ClearAllButton
            visible={selectedValues.length > 0}
            onClick={handleClearAll}
          >
            <X size={12} />
          </ClearAllButton>
          <ChevronIcon isOpen={isOpen} />
        </DropdownIcons>
      </DropdownButton>

      <DropdownMenu isOpen={isOpen}>
        {availableOptions.length === 0 ? (
          <div style={{ 
            padding: '16px', 
            textAlign: 'center', 
            color: '#64748b', 
            fontSize: '14px' 
          }}>
            All options selected
          </div>
        ) : (
          availableOptions.map(option => (
            <DropdownOption
              key={option.value}
              isSelected={false}
              onClick={() => handleOptionSelect(option.value)}
            >
              {option.label}
            </DropdownOption>
          ))
        )}
      </DropdownMenu>
    </DropdownContainer>
  );
};